import { useEffect, useState } from 'react';
import { ArrowLeft, Download, Loader2, Mail, Save, Users, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { RichTextEditor } from './RichTextEditor';
import { LetterTemplatesMenu } from './LetterTemplatesMenu';
import { CustomerPickerDialog } from './CustomerPickerDialog';
import { downloadLetterPdf, openLetterPdf } from './letterActions';

const RECIPIENT_FIELDS = [
  ['recipient_name', 'Name / Firma *'],
  ['recipient_contact', 'z. Hd. (optional)'],
  ['recipient_street', 'Straße & Nr.'],
  ['recipient_city', 'PLZ & Ort'],
  ['recipient_email', 'E-Mail (für Versand)'],
];

const CONTACT_FIELDS = [
  ['contact_name', 'Name'],
  ['contact_position', 'Position'],
  ['contact_phone', 'Telefon'],
  ['contact_email', 'E-Mail'],
  ['closing', 'Grußformel'],
];

const PanelCard = ({ title, children }) => (
  <div className="rounded-xl border bg-white p-4 space-y-3">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
    {children}
  </div>
);

const usePageSettings = () => {
  const [s, setS] = useState({});
  useEffect(() => { api.get('/admin/documents/settings').then(({ data }) => setS(data)).catch(() => {}); }, []);
  const logo = s.logo_url && s.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}` : s.logo_url;
  return {
    raw: s,
    logo,
    company: s.company_name || '',
    sender: (typeof s.header_sender_line === 'string' && s.header_sender_line) || [(s.company_name || '').replace(/\s*\bUG\b\s*(\([^)]*\))?|\s*\([^)]*\)/gi, ''), s.company_street, s.company_city].filter(Boolean).join(' · '),
    place: (s.company_city || '').replace(/^\s*\d{4,5}\s*/, ''),
  };
};

export const LetterEditor = ({ initial, onBack, onSaved, onEmail }) => {
  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(initial.id ? initial : null);
  const [dirty, setDirty] = useState(!initial.id);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);
  const page = usePageSettings();

  useEffect(() => {
    const st = page.raw;
    if (initial.id || !st.company_name) return;
    setForm((f) => (f.contact_name !== undefined ? f : {
      ...f,
      contact_name: st.company_owner || '',
      contact_position: st.company_owner ? 'Geschäftsführer' : '',
      contact_phone: st.company_phone || '',
      contact_email: st.company_email || '',
      closing: 'Mit freundlichen Grüßen',
    }));
  }, [page.raw, initial.id]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, letter_date: form.document_date };
      const { data } = saved
        ? await api.put(`/admin/letters/${saved.id}`, payload)
        : await api.post('/admin/letters', payload);
      setSaved(data);
      setForm((f) => ({ ...f, ...data }));
      setDirty(false);
      toast.success(`Brief ${data.document_number} gespeichert`);
      onSaved?.(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Brief konnte nicht gespeichert werden');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const withSaved = async (fn) => {
    const doc = dirty || !saved ? await save() : saved;
    if (doc) fn(doc);
  };

  return (
    <div className="space-y-5" data-testid="letter-editor">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <Button variant="ghost" onClick={onBack} className="-ml-2" data-testid="letter-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Alle Briefe
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 mr-1" data-testid="letter-save-state">
            {saved ? `${saved.document_number}${dirty ? ' · ungespeicherte Änderungen' : ' · gespeichert'}` : 'Neuer Brief'}
          </span>
          <Button variant="outline" size="sm" onClick={() => withSaved(openLetterPdf)} disabled={saving} data-testid="letter-preview-btn"><Eye className="w-4 h-4 mr-1" /> Vorschau</Button>
          <Button variant="outline" size="sm" onClick={() => withSaved(downloadLetterPdf)} disabled={saving} data-testid="letter-download-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={() => withSaved(onEmail)} disabled={saving} data-testid="letter-email-btn"><Mail className="w-4 h-4 mr-1" /> E-Mail</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#9B1B30] hover:bg-[#7a1526] text-white" data-testid="letter-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        <aside className="space-y-4 lg:sticky lg:top-4">
          <PanelCard title="Empfänger">
            {RECIPIENT_FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs text-gray-500">{label}</Label>
                <Input value={form[key] || ''} onChange={(e) => set({ [key]: e.target.value })} className="h-9" data-testid={`letter-${key.replace('_', '-')}-input`} />
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full" onClick={() => setPicker(true)} data-testid="letter-change-customer-btn">
              <Users className="w-4 h-4 mr-2" /> Kunde auswählen
            </Button>
          </PanelCard>
          <PanelCard title="Ansprechpartner & Unterschrift">
            {CONTACT_FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs text-gray-500">{label}</Label>
                <Input value={form[key] || ''} onChange={(e) => set({ [key]: e.target.value })} className="h-9" data-testid={`letter-${key.replace('_', '-')}-input`} />
              </div>
            ))}
          </PanelCard>
          <PanelCard title="Datum">
            <Input value={form.document_date || ''} onChange={(e) => set({ document_date: e.target.value })} placeholder="TT.MM.JJJJ" className="h-9" data-testid="letter-date-input" />
          </PanelCard>
          <PanelCard title="Vorlagen">
            <LetterTemplatesMenu subject={form.subject} bodyHtml={form.body_html} onApply={(t) => { set({ subject: t.subject || form.subject, body_html: t.body_html }); toast.success(`Vorlage „${t.name}" übernommen`); }} />
          </PanelCard>
        </aside>

        <div className="overflow-x-auto pb-4">
          <div className="mx-auto w-full max-w-[794px] min-h-[1123px] bg-white shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/5 px-8 sm:px-16 py-12" data-testid="letter-paper">
            <div className="flex justify-end h-16">{page.logo && <img src={page.logo} alt="Logo" className="max-h-16 object-contain" />}</div>
            <div className="relative h-[2px] mt-3 mb-6 bg-gray-200"><div className="absolute left-0 top-0 h-[2px] w-28 bg-[#9B1B30]" /></div>
            <div className="flex flex-col sm:flex-row justify-between gap-8 mb-14">
              <div className="text-[13px] leading-snug min-h-[120px] sm:w-[55%]">
                <p className="text-[9px] text-gray-500 border-b border-gray-300 pb-1 mb-2 inline-block whitespace-nowrap">{page.sender}</p>
                <p className="font-semibold">{form.recipient_name || <span className="text-gray-300">Empfänger</span>}</p>
                {form.recipient_contact && <p>z. Hd. {form.recipient_contact}</p>}
                <p>{form.recipient_street}</p>
                <p>{form.recipient_city}</p>
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px] content-start sm:mt-4" data-testid="letter-info-block">
                {[
                  ['Ihr Ansprechpartner', form.contact_name],
                  ['Telefon', form.contact_phone],
                  ['E-Mail', form.contact_email],
                  ['Kundennummer', form.customer_number],
                  ['Briefnummer', saved?.document_number],
                  ['Datum', `${page.place ? `${page.place}, ` : ''}${form.document_date || ''}`],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-[10px] text-gray-500 pt-[2px]">{k}</dt>
                    <dd className="text-gray-900 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <input
              value={form.subject || ''}
              onChange={(e) => set({ subject: e.target.value })}
              placeholder="Betreff"
              className="w-full font-bold text-[15px] mb-6 outline-none border-b border-transparent focus:border-[#9B1B30]/30 placeholder:text-gray-300 transition-colors"
              data-testid="letter-subject-input"
            />
            <RichTextEditor value={form.body_html} onChange={(html) => set({ body_html: html })} placeholder="Sehr geehrte Damen und Herren, ..." />
            {(form.closing || form.contact_name) && (
              <div className="mt-8 text-[14px]" data-testid="letter-signature-preview">
                {form.closing && <p>{form.closing}</p>}
                <p className="font-semibold mt-1">{page.company}</p>
                <div className="h-14 w-56 border-b border-gray-300 mb-1.5" />
                {form.contact_name && <p className="font-semibold">{form.contact_name}</p>}
                {form.contact_position && <p className="text-xs text-gray-500">{form.contact_position}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      <CustomerPickerDialog open={picker} onOpenChange={setPicker} onPick={(r) => { set(r); setPicker(false); }} />
    </div>
  );
};

export default LetterEditor;
